import * as React from 'react';
import axios from 'axios';
import { useHistory } from 'react-router';
import { API_BASE_URL, USER_TYPES } from '../../config';
import ACTIONS from '../actions.js';
import useNotification from '../notification/notificationContext';
import { NOTIFICATIONS  } from '../../config';

export const AppContext = React.createContext();

function useApp() {
  const context = React.useContext(AppContext);

  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }

  const [appState, dispatch] = context;

  const token = localStorage.getItem('jwtToken');
  if (token) axios.defaults.headers.common['x-access-token'] = token;

  const { setNotification } = useNotification();

  //login user
  const login = (data) => {
    localStorage.removeItem('jwtToken');
    
    dispatch({
      type: ACTIONS.LOGIN_START
    });

    
    axios.post(`${API_BASE_URL}/user/login`, data).then(res => {
      const response = res.data;

      axios.defaults.headers.common['x-access-token'] = response.data.token;
      localStorage.setItem('jwtToken', response.data.token);

      dispatch({
        type: ACTIONS.LOGIN_COMPLETE,
        payload: response.data.user
      });
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: "Login Success",
        message:
          "Successfully logged you in. Welcome to your XiViX Care Portal!",
      });
        
    }).catch(err => {
      dispatch({type: ACTIONS.LOGIN_FAILED})
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'An error occured',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });
      throw err;
    })
  }
  
  const getLoginType = (email) => {
    return axios.get(`${API_BASE_URL}/user/get-login-type/${encodeURIComponent(email)}`).then(res => {
      const response = res.data;
      return response.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'An error occured',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });
      throw err;
    })
  }

  //logout user
  const logout = () => {
    dispatch({
      type: ACTIONS.LOGOUT
    });
    delete axios.defaults.headers.common['x-access-token'];
    localStorage.removeItem('jwtToken');
  }

  //register user
  const register = (data) => {
    
    dispatch({type: ACTIONS.REGISTER_START});
    
    axios.post(`${API_BASE_URL}/user/register`, data).then(res => {
      const response = res.data;
      
      axios.defaults.headers.common['x-access-token'] = response.data.token;
      localStorage.setItem('jwtToken', response.data.token);
      
      dispatch({
        type: ACTIONS.REGISTER_COMPLETE,
        payload: response.data.user
      })
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Register success',
        message: 'Successfully registered your account. Welcome to the Xivix Robotic Portal!'
      });
    }).catch(err => {
      dispatch({type: ACTIONS.REGISTER_FAILED});
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'An error occured',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });
      throw err;
    })
  }

  //get current user
  const getCurrentUser = () => {
    axios.get(`${API_BASE_URL}/user/me`).then(res => {
      const response = res.data.data;
      const userId = response._id;
      getUserNotifications(userId);
      console.log('AUTHENTICATE COMPLETED');
      dispatch({
        type: ACTIONS.AUTHENTICATE_COMPLETE,
        payload: response
      })
    }).catch(err => {
      localStorage.removeItem('jwtToken');
      dispatch({ type: ACTIONS.AUTHENTICATE_FAILED })
    })
  }

  //update user info
  const updateUser = (data) => {
    axios.patch(`${API_BASE_URL}/user/update-user`, data).then(res => {
      const response = res.data;
      dispatch({
        type: ACTIONS.UPDATE_USER_COMPLETE,
        payload: response.data
      })
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });
      throw err;
    })
  }

  //update profile picture
  const updateProfilePicture = (file) => {
    var formData = new FormData();
    formData.append("profilePicture", file);
    return axios.post(`${API_BASE_URL}/file/profile-picture`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
    }).then(res => {
      const response = res.data;
      dispatch({
        type: ACTIONS.UPDATE_PROFILEPIC_COMPLETE,
        payload: response.data.Location
      })
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });
      throw err;
    })
  }

  //remove profile picture
  const removeProfilePicture = () => {
    axios.delete(`${API_BASE_URL}/file/profile-picture`, {
    }).then(() => {
      dispatch({
        type: ACTIONS.UPDATE_PROFILEPIC_COMPLETE,
        payload: ''
      })
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });
      throw err;
    })
  }

  //get partner types
  const getPartnerTypes = () => {
    axios.get(`${API_BASE_URL}/partner-type`).then(res => {
      const response = res.data;
      
      dispatch({
        type: ACTIONS.GET_PARTNER_TYPES_COMPLETE,
        payload: response.data
      })
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Warning',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });
      throw err;
    })
  }

  const updatePassword = (passwords) => {
    return axios.patch(`${API_BASE_URL}/user/update-password`, {newPassword: passwords.new, oldPassword: passwords.old}).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Password successfully updated.'
      });
      const userData = res.data.data;
      dispatch({
        type: ACTIONS.UPDATE_PASSWORD_SUCCESSFUL,
        payload: userData
      })
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data?.message || 'We were unable to update your password'
      })
      throw err;
    })
  }

  const updateEmail = (email) => {
    axios.patch(`${API_BASE_URL}/user/update-email`, email).then(response => {
      axios.defaults.headers.common['x-access-token'] = response.data.data.token;
      localStorage.setItem('jwtToken', response.data.data.token);
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Email successfully updated.'
      });
      dispatch({
        type: ACTIONS.UPDATE_EMAIL_SUCCESSFUL,
        payload: response.data.data.email
      })
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data?.message || 'We were unable to update your email'
      })
      throw err;
    })
  }

  const sendVerificationEmail = (email) => {
    const data = {
      email: email
    }
    axios.post(`${API_BASE_URL}/user/verify-account`, data).then(res => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.SUCCESS,
        title: 'Success',
        message: 'Verification email sent.'
      });
      return res;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data?.message || 'We were unable to send verification email.'
      })
      throw err;
    })
  }

  const getUserNotifications = (userId) => {
    axios.get(`${API_BASE_URL}/notification/me/get-all`).then(res => {
      dispatch({
        type: ACTIONS.GET_ALL_USER_NOTIFICATIONS,
        payload: res.data.data
      })
      return res;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: 'We were unable to get latest notifications.'
      })
      throw err;
    })
  }

  const refreshToken = async () => {
    await axios.get(`${API_BASE_URL}/user/refresh-token`).then(res => {
      localStorage.setItem('jwtToken', res.data.data);
      return res.data.data
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: 'We were unable to extend session'
      })
      throw err;
    })
  }

  const getAllMedicalQuestionnaire = () => {
    return axios.get(`${API_BASE_URL}/medical-questionnaire/get-all`).then(res => {
      return res.data;
    }).catch(err => {
      setNotification({
        show: true,
        type: NOTIFICATIONS.TYPE.WARNING_YELLOW,
        title: 'Request failed',
        message: err.response?.data.message || 'An error occured. Please try again later.'
      });
      throw err;
    })
  }

  return {
    appState,
    dispatch,
    login,
    logout,
    register,
    getCurrentUser,
    updateUser,
    getPartnerTypes,
    updateProfilePicture,
    removeProfilePicture,
    getLoginType,
    updatePassword,
    updateEmail,
    sendVerificationEmail,
    getUserNotifications,
    refreshToken,
    getAllMedicalQuestionnaire
  }
}

export default useApp;



